import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components/macro'

import { Accordion, Card } from '@loadsmart/loadsmart-ui'
import { Layout, Tag, Text } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'
import { getIconNameByMode, formatMode } from 'core/presenters/modePresenter'
import {
  formatAddress,
  formatDateOrAppointment,
  formatDescription,
  formatDescriptionWithZip,
  formatZip,
} from 'core/presenters/stopPresenter'
import { formatTemperature } from 'core/presenters/temperaturePresenter'
import { getTruckLabel } from 'core/presenters/truckPresenter'
import { ShippingItems } from 'core/shipping-items/ui/ShippingItems'
import { Icon } from 'core/ui/components/icon/Icon'
import { Item } from 'core/ui/components/item/Item'
import { LaneInformation } from 'core/ui/components/stop/LaneInformation'
import { StopsTimeline } from 'core/ui/components/stop/StopsTimeline'

import { ConfigProvider } from 'config/ui/ConfigContext'
import { ConfigRender } from 'config/ui/ConfigRender'
import { equipmentSubtypes } from 'tender/presenter/tenderPresenter'

import { type BulkRate } from '../domain/BulkRate'
import { type DraftQuote } from '../domain/DraftQuote'
import { isQuoteExpired, showAccessorials as modeSupportsAccessorials } from '../domain/Quote'
import { type AccessorialHistoryCharge, type AccessorialCharge } from '../domain/QuoteAccessorial'
import { getRateValue } from '../domain/Rate'
import { getFlatRate } from '../domain/RateLine'
import { type SpotQuote, canBookNowSpotQuote } from '../domain/SpotQuote'
import { useSpotQuote } from '../hooks/useSpotQuote'
import { formatDunnage, formatTarp } from '../presenter/spotQuotePresenter'
import { BidDeadline } from './BidDeadline'
import { BookNowBanner } from './BookNowBanner'
import { BookNowConfirmDialog } from './BookNowDialog'
import { BulkBidRateField } from './bulk/BulkBidFields'
import { QuoteDetails } from './QuoteDetails'
import { QuoteNotes } from './QuoteNotes'
import { ShipperAccessorialCharges } from './ShipperAccesorialCharges'
import { SingleBidRateField } from './SingleBidFields'
import { SpotQuoteBidDeadline } from './SpotQuoteBidDeadline'
import { ColumnWrapper, MoreInfoSectionWrapper } from './SpotQuoteDetails.styles'

export type SpotQuoteDetailsProps = Readonly<{
  id: string
  spotQuote: SpotQuote | BulkRate
  notes?: string
  quoteType?: 'single' | 'bulk'
  isSubmitted?: boolean
  currentFlatRate?: number
  disabled?: boolean
  accessorialsHistory?: Array<AccessorialHistoryCharge>
  onNotesChange: (notes: string) => void
  onRateChange: (flatRate: number) => void
  onAccessorialsChange: (accessorials: Array<AccessorialCharge>, rateUUID?: string) => void
  draftQuote?: DraftQuote
  submitDisabled?: boolean
  onSubmit?: () => void
  accessorialCharges?: Array<AccessorialCharge>
  handleRejectRate?: (reason: string) => void
}>

const StyledCard = styled(Card)`
  @media screen and (max-width: 820px) {
    width: 100%;
    overflow-x: auto;
  }
`

const StyledText = styled(Text)`
  :before {
    content: ' • ';
  }
`

export function SpotQuoteDetails({
  id,
  spotQuote,
  notes,
  quoteType = 'single',
  currentFlatRate,
  isSubmitted = false,
  disabled = false,
  accessorialsHistory,
  onRateChange,
  onNotesChange,
  onAccessorialsChange,
  draftQuote,
  submitDisabled,
  onSubmit,
  accessorialCharges,
  handleRejectRate,
}: SpotQuoteDetailsProps) {
  const { perMileRate, quote, submittedAt, status } = spotQuote
  const { mileage, stops, targetRate } = quote
  const canBookNow = canBookNowSpotQuote(spotQuote, disabled)
  const [hideBidFields, setHideBidFields] = useState(canBookNow)
  const [showBookNowDialog, setShowBookNowDialog] = useState(false)

  const flatRate = getFlatRate(spotQuote.ratelines) ?? spotQuote.flatRate

  const { bookNowQuote, bookNowSpotQuoteStatus } = useSpotQuote()

  const initialFlatRate = getRateValue(flatRate)
  const initialPerMileRate = getRateValue(perMileRate)
  const initialNotes = notes
  const isExpiredQuote = isQuoteExpired(quote)

  const isBidDeadlineMultiline = window.innerWidth < 600

  const isAccessorialSupportedByMode = modeSupportsAccessorials(quote)

  const showAccessorials = isSubmitted
    ? isAccessorialSupportedByMode
    : isAccessorialSupportedByMode && Boolean(accessorialsHistory)

  useEffect(() => {
    if (bookNowSpotQuoteStatus === RequestStatus.Error) {
      toast.error("Couldn't book. Please refresh the page")
    }
  }, [bookNowSpotQuoteStatus])

  async function handleBookNow() {
    setShowBookNowDialog(false)

    if (!draftQuote) return

    const { notes } = draftQuote
    const bookNowPayload = {
      id,
      notes,
      flatRate: Number(targetRate),
    }

    await bookNowQuote(bookNowPayload, quoteType)
  }

  function getFlatRateForAccessorialCharges() {
    if (hideBidFields) {
      return Number(targetRate)
    }
    return currentFlatRate ?? initialFlatRate
  }

  function getBidRateField() {
    return quoteType === 'single' ? (
      <SingleBidRateField
        isSubmitted={isSubmitted}
        mileage={mileage}
        initialFlatRate={initialFlatRate}
        initialPerMileRate={initialPerMileRate}
        disabled={disabled}
        onRateChange={onRateChange}
        currentFlatRate={currentFlatRate}
        submittedAt={submittedAt}
        submitDisabled={submitDisabled}
        accessorialCharges={accessorialCharges}
        onSubmit={onSubmit!}
        spotQuote={spotQuote as SpotQuote}
        handleRejectRate={handleRejectRate}
      />
    ) : (
      <BulkBidRateField
        isSubmitted={isSubmitted}
        mileage={mileage}
        initialFlatRate={initialFlatRate}
        initialPerMileRate={initialPerMileRate}
        disabled={disabled}
        onRateChange={onRateChange}
        currentFlatRate={currentFlatRate}
        submittedAt={submittedAt}
        rateStatus={status}
        spotQuote={spotQuote}
      />
    )
  }

  return (
    <StyledCard>
      <BidDeadline spotQuote={spotQuote} />
      <Card.Separator />
      <Card.Body>
        <Layout.Stack justify="center">
          <Layout.Group justify="space-between">
            <LaneInformation
              stops={stops}
              distance={mileage}
              valueFormatter={formatDescriptionWithZip}
              extraFormatter={stop => formatDateOrAppointment(stop)}
              distanceUnit="miles"
            />
            {hideBidFields ? (
              <BookNowBanner
                rate={targetRate}
                onCancel={() => setHideBidFields(false)}
                onConfirm={() => setShowBookNowDialog(true)}
                bookNowStatus={bookNowSpotQuoteStatus}
              />
            ) : (
              getBidRateField()
            )}
          </Layout.Group>
          {canBookNow && !hideBidFields && (
            <BookNowBanner
              rate={targetRate}
              onConfirm={() => setShowBookNowDialog(true)}
              bookNowStatus={bookNowSpotQuoteStatus}
            />
          )}
          {canBookNow && (
            <BookNowConfirmDialog
              isOpen={showBookNowDialog}
              targetRate={targetRate}
              onCancel={() => setShowBookNowDialog(false)}
              onConfirm={handleBookNow}
            />
          )}
          <Layout.Stack gap="spacing-8">
            {showAccessorials && (
              <ConfigProvider>
                <ConfigRender configName="enableQuoteAccessorials">
                  <ShipperAccessorialCharges
                    quoteAccessorials={quote.accessorials}
                    quoteRequestedAccessorials={quote.requestedAccessorials}
                    accessorialsHistory={accessorialsHistory}
                    flatRate={getFlatRateForAccessorialCharges()}
                    onAccessorialsChange={onAccessorialsChange}
                    isSubmitted={isSubmitted}
                    ratelines={spotQuote.ratelines}
                    rateUUID={spotQuote.uuid}
                    disabled={hideBidFields}
                  ></ShipperAccessorialCharges>
                </ConfigRender>
              </ConfigProvider>
            )}
            <Accordion initialExpanded>
              <Accordion.Toggle>
                <SpotQuoteBidDeadline
                  isExpired={isExpiredQuote}
                  deadline={quote.bidsDeadline}
                  isMultiline={isBidDeadlineMultiline}
                />
              </Accordion.Toggle>
              <Accordion.Body>
                <MoreInfoSectionWrapper>
                  <ColumnWrapper>
                    <Layout.Group align="center" gap="spacing-8" paddingB="spacing-6">
                      <Item
                        label="Mode"
                        value={
                          <Layout.Group align="flex-end" gap="spacing-1">
                            {formatMode(quote.mode)}
                            {quote.powerOnly && (
                              <Tag size="small" variant="warning">
                                power only
                              </Tag>
                            )}
                          </Layout.Group>
                        }
                        icon={<Icon name={getIconNameByMode(quote.mode)} height={20} width={20} />}
                      />
                      <Item
                        label="Equipment"
                        value={
                          <Layout.Group align="center" gap="spacing-1">
                            {getTruckLabel(quote.equipmentType)}
                            {quote.equipmentType === 'RFR' && (
                              <StyledText>
                                {formatTemperature(
                                  quote.equipmentRequirements?.temperature,
                                  quote.equipmentRequirements?.temperatureUnit
                                )}
                              </StyledText>
                            )}
                          </Layout.Group>
                        }
                      />
                      {quote.equipmentRequirements?.equipmentSubtypes && (
                        <Item
                          label="Sub equipment"
                          value={equipmentSubtypes[quote.equipmentRequirements.equipmentSubtypes]}
                        />
                      )}
                      {quote.equipmentRequirements && (
                        <>
                          <Item
                            label="Tarp"
                            value={formatTarp(
                              quote.equipmentRequirements.tarpType,
                              quote.equipmentRequirements.tarpSize
                            )}
                          />
                          <Item
                            label="Dunnage"
                            value={formatDunnage(quote.equipmentRequirements.dunnage)}
                          />
                        </>
                      )}
                    </Layout.Group>
                    <StopsTimeline
                      stops={stops}
                      titleFormatter={formatDescription}
                      superTitleFormatter={formatAddress}
                      subTitleFormatter={formatZip}
                      tags
                    />
                  </ColumnWrapper>
                  <ColumnWrapper>
                    <QuoteDetails quote={quote} />
                    <QuoteNotes
                      initialNotes={initialNotes}
                      onChange={onNotesChange}
                      disabled={disabled}
                    />
                  </ColumnWrapper>
                  <ColumnWrapper>
                    <ShippingItems shippingItems={quote.items} measures={quote.measures} />
                  </ColumnWrapper>
                </MoreInfoSectionWrapper>
              </Accordion.Body>
            </Accordion>
          </Layout.Stack>
        </Layout.Stack>
      </Card.Body>
    </StyledCard>
  )
}
